import {
  Container,
  Title,
  Table,
  Text,
  Group,
  Card,
  Badge,
  ActionIcon,
  Tooltip,
  Center,
  Loader,
  Stack,
  Alert,
  Button,
  Modal,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconUsers,
  IconAlertCircle,
  IconEye,
  IconFileText,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  usePendingUsers,
  useApproveUser,
  useRejectUser,
  usePendingHealthCertificates,
  useApproveHealthCertificate,
  useRejectHealthCertificate,
} from '../../../hooks/useVolunteer';
import { notifications } from '@mantine/notifications';
import {
  protectedVolunteerApi,
  type PendingHealthCertificateResponse,
  type PendingUserResponse,
} from '../../../api/volunteer';
import { BackLink } from '@/components/BackLink';
import { useEffect, useState } from 'react';

export default function PendingUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: pendingUsers, isLoading, error } = usePendingUsers();
  const approveUser = useApproveUser();
  const rejectUser = useRejectUser();
  const { data: pendingHealthCertificates } = usePendingHealthCertificates();
  const approveHealthCertificate = useApproveHealthCertificate();
  const rejectHealthCertificate = useRejectHealthCertificate();
  const [userToReject, setUserToReject] = useState<PendingUserResponse | null>(
    null
  );
  const [certificateToReject, setCertificateToReject] =
    useState<PendingHealthCertificateResponse | null>(null);
  const [certificateToPreview, setCertificateToPreview] =
    useState<PendingHealthCertificateResponse | null>(null);
  const [certificatePreviewUrl, setCertificatePreviewUrl] = useState<
    string | null
  >(null);
  const [certificatePreviewType, setCertificatePreviewType] = useState('');
  const [certificatePreviewLoading, setCertificatePreviewLoading] =
    useState(false);
  const [certificatePreviewError, setCertificatePreviewError] = useState(false);

  useEffect(() => {
    if (!certificateToPreview) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    protectedVolunteerApi
      .getHealthCertificate(certificateToPreview.volunteerId)
      .then(blob => {
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setCertificatePreviewUrl(objectUrl);
        setCertificatePreviewType(blob.type);
      })
      .catch(() => {
        if (!cancelled) setCertificatePreviewError(true);
      })
      .finally(() => {
        if (!cancelled) setCertificatePreviewLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [certificateToPreview]);

  const openCertificatePreview = (
    certificate: PendingHealthCertificateResponse
  ) => {
    setCertificatePreviewLoading(true);
    setCertificatePreviewError(false);
    setCertificatePreviewUrl(null);
    setCertificatePreviewType('');
    setCertificateToPreview(certificate);
  };

  const handleApprove = async (userId: string, userName: string) => {
    try {
      await approveUser.mutateAsync(userId);
      notifications.show({
        title: t('admin.pendingUsers.notifications.approved'),
        message: t('admin.pendingUsers.notifications.approvedMessage', {
          name: userName,
        }),
        color: 'green',
      });
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.pendingUsers.notifications.error'),
        color: 'red',
      });
    }
  };

  const handleReject = async (userId: string, userName: string) => {
    try {
      await rejectUser.mutateAsync(userId);
      notifications.show({
        title: t('admin.pendingUsers.notifications.rejected'),
        message: t('admin.pendingUsers.notifications.rejectedMessage', {
          name: userName,
        }),
        color: 'orange',
      });
      setUserToReject(null);
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.pendingUsers.notifications.error'),
        color: 'red',
      });
    }
  };

  const handleHealthCertificateReview = async (
    volunteerId: string,
    volunteerName: string,
    decision: 'approve' | 'reject'
  ) => {
    try {
      if (decision === 'approve') {
        await approveHealthCertificate.mutateAsync(volunteerId);
      } else {
        await rejectHealthCertificate.mutateAsync(volunteerId);
      }
      const resultKey = decision === 'approve' ? 'approved' : 'rejected';
      notifications.show({
        title: t(`admin.pendingUsers.healthCertificates.${resultKey}`),
        message: t(
          `admin.pendingUsers.healthCertificates.${resultKey}Message`,
          { name: volunteerName }
        ),
        color: decision === 'approve' ? 'green' : 'orange',
      });
      if (decision === 'reject') setCertificateToReject(null);
    } catch {
      notifications.show({
        title: t('common.error'),
        message: t('admin.pendingUsers.notifications.error'),
        color: 'red',
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container size="md" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title={t('common.error')}
          color="red"
        >
          {t('admin.pendingUsers.error')}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <BackLink to="/app/dashboard" />
      <Group justify="space-between" mb="xl">
        <Group>
          <IconUsers size={32} />
          <Title order={2}>{t('admin.pendingUsers.title')}</Title>
        </Group>
        <Badge size="lg" variant="light" color="yellow">
          {(pendingUsers?.length || 0) +
            (pendingHealthCertificates?.length || 0)}{' '}
          {t('admin.pendingUsers.pending')}
        </Badge>
      </Group>

      {!pendingUsers || pendingUsers.length === 0 ? (
        <Card withBorder padding="xl" radius="md">
          <Stack align="center" gap="md">
            <IconUsers size={48} color="gray" />
            <Text c="dimmed" ta="center">
              {t('admin.pendingUsers.empty')}
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card withBorder padding={0} radius="md">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin.pendingUsers.table.name')}</Table.Th>
                <Table.Th>{t('admin.pendingUsers.table.email')}</Table.Th>
                <Table.Th>
                  {t('admin.pendingUsers.table.registeredAt')}
                </Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t('admin.pendingUsers.table.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pendingUsers.map((user: PendingUserResponse) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Stack gap={4} align="flex-start">
                      <Text fw={500}>{user.volunteerName}</Text>
                      {user.mapsToPlaceholder && (
                        <Badge size="xs" color="blue" variant="light">
                          {t('admin.pendingUsers.mapsToPlaceholder')}
                        </Badge>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {user.email}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label={t('admin.pendingUsers.actions.view')}>
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="lg"
                          onClick={() =>
                            navigate(
                              `/app/admin/volunteers/${user.volunteerId}?from=pending-users`
                            )
                          }
                        >
                          <IconEye size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('admin.pendingUsers.actions.approve')}>
                        <ActionIcon
                          variant="light"
                          color="green"
                          size="lg"
                          onClick={() =>
                            handleApprove(user.id, user.volunteerName)
                          }
                          loading={approveUser.isPending}
                        >
                          <IconCheck size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('admin.pendingUsers.actions.reject')}>
                        <ActionIcon
                          variant="light"
                          color="red"
                          size="lg"
                          onClick={() => setUserToReject(user)}
                          disabled={rejectUser.isPending}
                        >
                          <IconX size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Title order={3} mt="xl" mb="md">
        {t('admin.pendingUsers.healthCertificates.title')}
      </Title>
      {!pendingHealthCertificates || pendingHealthCertificates.length === 0 ? (
        <Card withBorder padding="xl" radius="md">
          <Text c="dimmed" ta="center">
            {t('admin.pendingUsers.healthCertificates.empty')}
          </Text>
        </Card>
      ) : (
        <Card withBorder padding={0} radius="md">
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('admin.pendingUsers.table.name')}</Table.Th>
                <Table.Th>{t('admin.pendingUsers.table.email')}</Table.Th>
                <Table.Th>
                  {t('admin.pendingUsers.healthCertificates.uploadedAt')}
                </Table.Th>
                <Table.Th>{t('admin.pendingUsers.table.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {pendingHealthCertificates.map(certificate => (
                <Table.Tr key={certificate.volunteerId}>
                  <Table.Td>
                    <Text fw={500}>{certificate.volunteerName}</Text>
                    {certificate.originalName && (
                      <Text size="xs" c="dimmed">
                        {certificate.originalName}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{certificate.email}</Table.Td>
                  <Table.Td>
                    {certificate.uploadedAt
                      ? new Date(certificate.uploadedAt).toLocaleDateString()
                      : '–'}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip
                        label={t('admin.pendingUsers.healthCertificates.view')}
                      >
                        <ActionIcon
                          variant="light"
                          color="blue"
                          size="lg"
                          onClick={() => openCertificatePreview(certificate)}
                        >
                          <IconFileText size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <ActionIcon
                        variant="light"
                        color="green"
                        size="lg"
                        loading={approveHealthCertificate.isPending}
                        onClick={() =>
                          void handleHealthCertificateReview(
                            certificate.volunteerId,
                            certificate.volunteerName,
                            'approve'
                          )
                        }
                      >
                        <IconCheck size={18} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="red"
                        size="lg"
                        loading={rejectHealthCertificate.isPending}
                        onClick={() => setCertificateToReject(certificate)}
                      >
                        <IconX size={18} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Modal
        opened={certificateToPreview !== null}
        onClose={() => setCertificateToPreview(null)}
        title={t('admin.pendingUsers.healthCertificates.previewDialog.title', {
          name: certificateToPreview?.volunteerName,
        })}
        size="xl"
        styles={{
          body: { padding: 0, height: '80vh' },
          content: { height: '85vh' },
        }}
      >
        {certificatePreviewLoading && (
          <Center h="100%">
            <Loader />
          </Center>
        )}
        {certificatePreviewError && (
          <Center h="100%" p="xl">
            <Alert
              icon={<IconAlertCircle size={16} />}
              title={t('common.error')}
              color="red"
            >
              {t('admin.pendingUsers.healthCertificates.previewDialog.error')}
            </Alert>
          </Center>
        )}
        {certificatePreviewUrl &&
          (certificatePreviewType.startsWith('image/') ? (
            <Center h="100%" p="md">
              <img
                src={certificatePreviewUrl}
                alt={t(
                  'admin.pendingUsers.healthCertificates.previewDialog.imageAlt',
                  { name: certificateToPreview?.volunteerName }
                )}
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </Center>
          ) : (
            <iframe
              src={certificatePreviewUrl}
              title={t(
                'admin.pendingUsers.healthCertificates.previewDialog.title',
                { name: certificateToPreview?.volunteerName }
              )}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ))}
      </Modal>

      <Modal
        opened={certificateToReject !== null}
        onClose={() => setCertificateToReject(null)}
        title={t('admin.pendingUsers.healthCertificates.rejectDialog.title')}
        centered
        closeOnClickOutside={!rejectHealthCertificate.isPending}
        closeOnEscape={!rejectHealthCertificate.isPending}
        withCloseButton={!rejectHealthCertificate.isPending}
      >
        <Stack>
          <Text>
            {t('admin.pendingUsers.healthCertificates.rejectDialog.message', {
              name: certificateToReject?.volunteerName,
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('admin.pendingUsers.healthCertificates.rejectDialog.details')}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              disabled={rejectHealthCertificate.isPending}
              onClick={() => setCertificateToReject(null)}
            >
              {t('admin.pendingUsers.rejectDialog.cancel')}
            </Button>
            <Button
              color="red"
              loading={rejectHealthCertificate.isPending}
              onClick={() => {
                if (certificateToReject) {
                  void handleHealthCertificateReview(
                    certificateToReject.volunteerId,
                    certificateToReject.volunteerName,
                    'reject'
                  );
                }
              }}
            >
              {t('admin.pendingUsers.healthCertificates.rejectDialog.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={userToReject !== null}
        onClose={() => setUserToReject(null)}
        title={t('admin.pendingUsers.rejectDialog.title')}
        centered
        closeOnClickOutside={!rejectUser.isPending}
        closeOnEscape={!rejectUser.isPending}
        withCloseButton={!rejectUser.isPending}
      >
        <Stack>
          <Text>
            {t('admin.pendingUsers.rejectDialog.message', {
              name: userToReject?.volunteerName,
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {t('admin.pendingUsers.rejectDialog.details')}
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setUserToReject(null)}
              disabled={rejectUser.isPending}
            >
              {t('admin.pendingUsers.rejectDialog.cancel')}
            </Button>
            <Button
              color="red"
              loading={rejectUser.isPending}
              onClick={() => {
                if (userToReject) {
                  void handleReject(
                    userToReject.id,
                    userToReject.volunteerName
                  );
                }
              }}
            >
              {t('admin.pendingUsers.rejectDialog.confirm')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}
